/**
 * Filters.tsx
 *
 * Renders the full suite of UI filter controls for the search page:
 * - Keyword search input
 * - Selected filter pills
 * - Price slider (range)
 * - Checkbox filters grouped by facet
 *
 * Supports merging of similar/variant filters using `MERGE_FILTERS_MAP`.
 * Example: "US", "USA", "U.S." → "United States"
 *
 * Props:
 * - `aggs`: Facet aggregations from current search results
 * - `originalAggs`: Full facet list from unfiltered search (for full filter menu)
 * - `facets`: Currently selected facets
 * - `setFacets`: Updater for modifying facet selections
 * - `query`: User-entered search query
 * - `setQuery`: Updater for query input
 */

'use client';

import React, {
  FunctionComponent,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { usePathname } from 'next/navigation';

// Components
import PriceFilter from './priceFilter';
import SearchFilter from './searchFilter';
import CheckFilter from './checkFilter';
import SelectedFilter from './selectedFilter';

// Config
import { MERGE_FILTERS_MAP } from '@/lib/config/search/filterMergeMapping';
import { SEARCH_KEY_MAPPING } from '@/lib/config/search/searchKeyMapping';
import { GLOBAL_EXCLUDED_FILTERS } from '@/lib/config/search/global';

// Types
import {
  Filter,
  Filters as FiltersType,
  FilterGroup,
} from '@/lib/types/filters';

import styles from './searchFilters.module.scss';

// Aggregation result types from the search API
export type AggregationValue = {
  label: string;
  value: string;
  count: number;
};

export type PriceRangeValues = {
  min_price: number;
  max_price: number;
};

export type AggregationGroup =
  | {
      keyword: 'price_range';
      label: string;
      values: PriceRangeValues;
    }
  | {
      keyword: string;
      label: string;
      values: AggregationValue[];
    };

// Type guard for identifying price range aggregation
function isPriceRangeObject(values: unknown): values is PriceRangeValues {
  if (
    typeof values === 'object' &&
    values !== null &&
    'min_price' in values &&
    'max_price' in values
  ) {
    const v = values as Record<string, unknown>;
    return (
      typeof v.min_price === 'number' && typeof v.max_price === 'number'
    );
  }
  return false;
}

interface FiltersProps {
  title: string;
  aggs: AggregationGroup[];
  originalAggs: AggregationGroup[];
  facets: Record<string, string | PriceRangeValues>;
  setFacets: React.Dispatch<
    React.SetStateAction<Record<string, string | PriceRangeValues>>
  >;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  // Aggs from the query's first result set; items at zero there stay hidden, not grayed.
  initialAggs?: AggregationGroup[];
}

// Sums counts for the given raw facet values within the matching aggregation group
function sumAggCounts(
  groups: AggregationGroup[],
  keyword: string,
  rawValues: string[]
): number {
  const matchingAgg = groups.find((a) => a.keyword === keyword);
  if (!matchingAgg || isPriceRangeObject(matchingAgg.values)) return 0;
  const valuesArray = matchingAgg.values as AggregationValue[];
  return rawValues
    .map((val) => valuesArray.find((v) => v.value === val)?.count ?? 0)
    .reduce((sum, c) => sum + c, 0);
}

const Filters: FunctionComponent<FiltersProps> = ({
  title,
  aggs,
  originalAggs,
  facets,
  setFacets,
  query,
  setQuery,
  initialAggs = [],
}) => {
  const pathname = usePathname();
  const cleanPathname = pathname.replace(/^\/|\/$/g, '');
  const config = SEARCH_KEY_MAPPING[cleanPathname] ?? undefined;

  // Memoise per-page exclusions so the reference is stable
  const perPageExcludes = useMemo<string[]>(
    () => config?.filters?.exclude ?? [],
    [config]
  );

  const [selectedFilters, setSelectedFilters] = useState<FiltersType>([]);
  const [priceValue, setValue] = useState<[number, number]>([0, 100]);
  const [initialPriceRange, setInitialPriceRange] = useState<{
    min: number;
    max: number;
  } | null>(null);

  // Extract min/max price from originalAggs once
  useEffect(() => {
    if (!initialPriceRange) {
      const priceAgg = originalAggs.find(
        (a) => a.keyword === 'price_range'
      );
      if (priceAgg && isPriceRangeObject(priceAgg.values)) {
        setInitialPriceRange({
          min: 1,
          max: Math.ceil(priceAgg.values.max_price / 100),
        });
      }
    }
  }, [originalAggs, initialPriceRange]);

  // Convert facet values to selected filter objects
  useEffect(() => {
    const newFilters: FiltersType = [];

    for (const [key, raw] of Object.entries(facets)) {
      if (key === 'price_range') continue;

      // Map variants (e.g., US, USA) to canonical labels
      const mergeMap = MERGE_FILTERS_MAP[key] || {};
      const inverseMap: Record<string, string> = {};
      for (const [canonical, variants] of Object.entries(mergeMap)) {
        for (const v of variants) {
          inverseMap[v.toLowerCase()] = canonical;
        }
      }

      const values = typeof raw === 'string' ? raw.split(',') : [];
      values.forEach((value) => {
        const canonical = inverseMap[value.toLowerCase()] || value;
        newFilters.push({ parameter: key, value, name: canonical });
      });
    }

    setSelectedFilters(newFilters);
  }, [facets]);

  // Generate checkbox filter groups
  const filters: FilterGroup[] = useMemo(() => {
    return originalAggs
      .filter(
        (
          agg
        ): agg is Extract<
          AggregationGroup,
          { values: AggregationValue[] }
        > => {
          const isGloballyExcluded =
            GLOBAL_EXCLUDED_FILTERS.includes(agg.keyword);
          const isPageExcluded = perPageExcludes.includes(agg.keyword);
          return (
            agg.keyword !== 'price_range' &&
            !isGloballyExcluded &&
            !isPageExcluded
          );
        }
      )
      .map((agg) => {
        const mergeMap = MERGE_FILTERS_MAP[agg.keyword] || {};
        const mergedItems: Record<string, AggregationValue> = {};

        // Merge values by canonical label
        agg.values.forEach((item) => {
          const label = item.label.trim();
          if (!label) return;

          let canonicalLabel = label;
          for (const [target, synonyms] of Object.entries(mergeMap)) {
            if (
              synonyms
                .map((s) => s.toLowerCase())
                .includes(label.toLowerCase())
            ) {
              canonicalLabel = target;
              break;
            }
          }

          if (!mergedItems[canonicalLabel]) {
            mergedItems[canonicalLabel] = {
              label: canonicalLabel,
              value: canonicalLabel,
              count: item.count,
            };
          } else {
            mergedItems[canonicalLabel].count += item.count;
          }
        });

        // Sort by unit-aware value (for size.keyword) or alphabetically
        const sortedItems = Object.values(mergedItems).sort((a, b) => {
          if (agg.keyword === 'size.keyword') {
            const toMilliliters = (label: string): number => {
              const input = label
                .trim()
                .toLowerCase()
                .replace(/\s+/g, '');
              const match = input.match(
                /^([\d.]+)(ml|milliliter|milliliters|l|liter|litre|liters|litres|oz|floz|ounce|ounces|gal|gallon|gallons)$/
              );
              if (!match) return Infinity;
              const value = parseFloat(match[1]);
              const unit = match[2];
              switch (unit) {
                case 'ml':
                case 'milliliter':
                case 'milliliters':
                  return value;
                case 'l':
                case 'liter':
                case 'litre':
                case 'liters':
                case 'litres':
                  return value * 1000;
                case 'oz':
                case 'floz':
                case 'ounce':
                case 'ounces':
                  return value * 29.5735;
                case 'gal':
                case 'gallon':
                case 'gallons':
                  return value * 3785.41;
                default:
                  return Infinity;
              }
            };
            return toMilliliters(a.label) - toMilliliters(b.label);
          }
          return a.label.localeCompare(b.label);
        });

        // Build checkbox item group
        return {
          label: agg.label,
          parameter: agg.keyword,
          canSelect: 9999,
          items: sortedItems.flatMap((item) => {
            const rawValues =
              Object.entries(mergeMap).find(
                ([target]) => target === item.label
              )?.[1] ?? [item.value];

            const isChecked = selectedFilters.some(
              (f) =>
                f.parameter === agg.keyword &&
                rawValues.includes(f.value)
            );

            // Sum count across merged variants using live aggs
            const count = sumAggCounts(aggs, agg.keyword, rawValues);

            // Visibility keys off the first-paint counts; falls back to live counts until the snapshot exists
            const initialCount =
              initialAggs.length > 0
                ? sumAggCounts(initialAggs, agg.keyword, rawValues)
                : count;

            // Items at zero on first paint stay hidden for the query; checked items always render so a selected facet stays removable (FEL-3754)
            if (initialCount === 0 && !isChecked) return [];

            return [{
              label: `${item.label} (${count})`,
              value: item.value,
              count,
              disabled: count === 0,
              checked: isChecked,
            }];
          }),
        };
      })
      // Hide categories with fewer than 2 options; groups holding a checked item stay visible so it can be unselected (FEL-3754)
      .filter(
        (group) =>
          group.items.length > 1 ||
          group.items.some((item) => item.checked)
      );
  }, [originalAggs, perPageExcludes, aggs, selectedFilters, initialAggs]);

  // Sync price slider on load
  useEffect(() => {
    if (initialPriceRange) {
      setValue([initialPriceRange.min, initialPriceRange.max]);
    }
  }, [initialPriceRange]);

  // Add filter to facet state
  const addFilter = (newFilter: Filter) => {
    const mergeMap = MERGE_FILTERS_MAP[newFilter.parameter] || {};
    const rawValues =
      Object.entries(mergeMap).find(
        ([canonical]) => canonical === newFilter.value
      )?.[1] || [newFilter.value];

    const raw = facets[newFilter.parameter];
    const existing = typeof raw === 'string' ? raw.split(',') : [];
    const merged = Array.from(new Set([...existing, ...rawValues]));

    setFacets({
      ...facets,
      [newFilter.parameter]: merged.join(','),
    });
  };

  // Remove filter and update facets
  const removeFilter = ({ parameter, value }: Filter) => {
    const mergeMap = MERGE_FILTERS_MAP[parameter] || {};
    const rawValues =
      Object.entries(mergeMap).find(
        ([canonical]) => canonical === value
      )?.[1] || [value];

    const raw = facets[parameter];
    const values = typeof raw === 'string' ? raw.split(',') : [];
    const filtered = values.filter(
      (v: string) => !rawValues.includes(v)
    );

    const next = { ...facets };
    if (filtered.length) {
      next[parameter] = filtered.join(',');
    } else {
      delete next[parameter];
    }

    setFacets(next);
  };

  // Update price facet
  const priceFilter = (values: number[]) => {
    const [min, max] = values;
    setFacets({
      ...facets,
      price_range: {
        min_price: min * 100,
        max_price: max * 100,
      },
    });
  };

  const onPriceChange = (val: number | number[]) => {
    if (Array.isArray(val) && val.length === 2) {
      setValue([val[0], val[1]]);
    }
  };

  return (
    <div className={styles.filterWrapper}>
      <SearchFilter title={title} query={query} setQuery={setQuery} />
      <SelectedFilter data={selectedFilters} remove={removeFilter} />
      {initialPriceRange && (
        <PriceFilter
          onChange={onPriceChange}
          onSubmit={priceFilter}
          value={priceValue}
          min={initialPriceRange.min}
          max={initialPriceRange.max}
          step={1}
        />
      )}
      <CheckFilter
        data={{ filters }}
        selectedFilters={selectedFilters}
        add={addFilter}
        remove={removeFilter}
      />
    </div>
  );
};

export default Filters;
